/**
 * Unveil2.js
 * A very lightweight jQuery plugin to lazy load images
 * Based on https://github.com/luis-almeida/unveil
 *
 * Licensed under the MIT license.
 * Copyright 2015 Joram van den Boezem
 * https://github.com/nabble/unveil2
 *
 * Modified by: Evgeny Khovansky <fajesu@ya.ru>
 *        Date: July 29, 2025
 *     Changes: Refactoring
 *              Removed deprecated options
 *              Changed loaded function logic for elements without source attribute
 *              Added option to disable setting background for not IMG elements
 *              Added passing along data argument to custom load events
 *              Updated "inview" function
 *              Added check for parent triggered in actual logic function
 */
(function(factory) {
    'use strict';

    if (typeof define === 'function' && define.amd)
        define(['jquery'], factory);
    else if (typeof exports !== 'undefined')
        module.exports = factory(require('jquery'));
    else
        factory(jQuery);

}(function ($) {

    "use strict";

    /**
     * # GLOBAL VARIABLES
     * ---
     */

        /**
         * Store the string 'unveil' in a variable to save some bytes
         */
    let unveilString = 'unveil',

        /**
         * jQuery event namespace
         */
        unveilNamespace = '.' + unveilString,

        /**
         * Store the string 'src' in a variable to save some bytes
         */
        srcString = 'src',

        /**
         * Store the string 'placeholder' in a variable to save some bytes
         */
        placeholderString = 'placeholder';

    /**
     * # PLUGIN
     * ---
     */

    /**
     * @param {object} options An object of options, see API section in README
     * @returns {$}
     */
    $.fn.unveil = function (options) {

        options = options || {};

        // Initialize variables
        let $window = $(window),
            height = $window.height(),
            defaults = {
                // Public API
                placeholder: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
                offset: 0,
                breakpoints: [],
                throttle: 250,
                debug: false,
                attribute: srcString,
                container: $window,
                backgroundSet: true,

                // Undocumented
                retina: window.devicePixelRatio > 1
            },
            settings = $.extend(true, {}, defaults, options);

        if (settings.debug) console.log('Called unveil on', this.length, 'elements with the following options:', settings);

        /**
         * Sort sizes array, arrange highest minWidth to front of array
         */
        settings.breakpoints.sort(function(a, b) {
            return b.minWidth - a.minWidth;
        });

        let containerContext = settings.container.data(unveilString);

        if (!containerContext) {
            containerContext = {
                images: $(),
                initialized: false
            };
            settings.container.data(unveilString, containerContext);
        }

        /**
         * # UNVEIL IMAGES
         * ---
         */

        /**
         * This is the actual plugin logic, which determines the source attribute to use based on window width and presence of a retina screen, changes the source of the image, handles class name changes and triggers a callback if set. Once the image has been loaded, start the unveil lookup because the page layout could have changed.
         */
        this.one(unveilString + unveilNamespace, function (e) {
            // Fix for parent triggered (e.stopPropagation() not working)
            if (e.target !== e.currentTarget)
                return;

            let $this = $(this),
                windowWidth = document.documentElement.clientWidth || Math.round(window.visualViewport.width) || $window.width(),
                attrib = settings.attribute, targetSrc, defaultSrc, retinaSrc,
                loadCallback = function(e, loadData) {
                    // Change classes
                    classLoaded($this);

                    // Trigger custom event
                    $this.trigger('loaded' + unveilNamespace, [loadData]);

                    // Loading the image may have modified page layout,
                    // so unveil again
                    lookup();
                };

            // Determine attribute to extract source from
            for (let i = 0; i < settings.breakpoints.length; i++) {
                let dataAttrib = settings.breakpoints[i].attribute.replace(/^data-/, '');

                if (windowWidth >= settings.breakpoints[i].minWidth && $this.data(dataAttrib)) {
                    attrib = dataAttrib;
                    break;
                }
            }

            // Extract source
            defaultSrc = retinaSrc = $this.data(attrib);

            // Do we have a retina source?
            if (defaultSrc && defaultSrc.indexOf('|') !== -1) {
                retinaSrc = defaultSrc.split('|')[1];
                defaultSrc = defaultSrc.split('|')[0];
            }

            // Change attribute on image
            if (defaultSrc) {
                targetSrc = (settings.retina && retinaSrc) ? retinaSrc : defaultSrc;

                let loadData = {
                    attribute: attrib,
                    retina: settings.retina,
                    defaultSrc: defaultSrc,
                    retinaSrc: retinaSrc,
                    targetSrc: targetSrc
                };

                if (settings.debug) console.log('Unveiling image', loadData);

                // Change classes
                $this.addClass(unveilString + '-loading');

                // Trigger custom event
                $this.trigger('loading' + unveilNamespace, [loadData]);

                // When new source has loaded, do stuff
                $this.one('load' + unveilNamespace, loadCallback);

                // Set new source
                if (this.nodeName === 'IMG')
                    $this.prop(srcString, targetSrc);
                else {
                    $('<img/>').attr(srcString, targetSrc).one('load' + unveilNamespace, function() {
                        $(this).remove();

                        if (settings.backgroundSet)
                            $this.css('backgroundImage', 'url(' + targetSrc + ')');

                        $this.trigger('load' + unveilNamespace, [loadData]);
                    });
                }

                // If the image has instantly loaded, change classes now
                if (this.complete)
                    classLoaded($this);
            }
            else
                loadCallback();
        });

        this.one('destroy' + unveilNamespace, function() {
            $(this).off(unveilNamespace).removeData(unveilString);

            if (containerContext.images) {
                containerContext.images = containerContext.images.not(this);

                if (!containerContext.images.length)
                    destroyContainer();
            }
        });

        /**
         * # HELPER FUNCTIONS
         * ---
         */

        /**
         * Sets the classes when an image is done loading
         *
         * @param {object} $elm
         */
        function classLoaded($elm) {
            $elm
                .removeClass(unveilString + '-' + placeholderString + ' ' + unveilString + '-loading')
                .addClass(unveilString + '-loaded');
        }

        /**
         * Filter function which returns true when a given image is in the viewport.
         *
         * @returns {boolean}
         */
        function inview() {
            let $this = $(this);

            if ($this.is(':hidden') || $this.data(unveilString + 'Skip'))
                return;

            let _settings = $this.data(unveilString) || settings,
                viewport = {top: 0 - _settings.offset, bottom: $window.height() + _settings.offset},
                isCustomContainer = _settings.container[0] !== $window[0],
                elementRect = this.getBoundingClientRect();

            if (isCustomContainer) {
                let containerRect = _settings.container[0].getBoundingClientRect();

                if (contains(viewport, containerRect)) {
                    let top = containerRect.top - _settings.offset,
                        bottom = containerRect.bottom + _settings.offset,
                        containerRectWithOffset = {
                            top: top > viewport.top ? top : viewport.top,
                            bottom: bottom < viewport.bottom ? bottom : viewport.bottom
                        };

                    return contains(containerRectWithOffset, elementRect);
                }

                return false;
            }
            else
                return contains(viewport, elementRect);
        }

        /**
         * Whether `viewport` contains `rect` vertically
         */
        function contains(viewport, rect) {
            return rect.bottom >= viewport.top && rect.top <= viewport.bottom;
        }

        /**
         * Sets the window height and calls the lookup function
         */
        function resize() {
            height = $window.height();
            lookup();
        }

        /**
         * Throttle function with function call in tail. Based on http://sampsonblog.com/749/simple-throttle-function
         *
         * @param {function} callback
         * @returns {function}
         */
        function throttle(callback) {
            let wait = false;            // Initially, we're not waiting

            return function() {             // We return a throttled function
                if (!wait) {                      // If we're not waiting
                    wait = true;                  // Prevent future invocations
                    setTimeout(function() { // After a period of time
                        callback();               // Execute users function
                        wait = false;             // And allow future invocations
                    }, settings.throttle);
                }
            };
        }

        /**
         * # LOOKUP FUNCTION
         * ---
         */

        /**
         * Function which filters images which are in view and triggers the unveil event on those images.
         */
        function lookup() {
            if (settings.debug) console.log('Unveiling');

            if (containerContext.images && containerContext.images.length) {
                let batch = containerContext.images.filter(inview);

                batch.trigger(unveilString + unveilNamespace);
                containerContext.images = containerContext.images.not(batch);

                if (batch.length)
                    if (settings.debug) console.log('New images in view', batch.length, ', leaves', containerContext.length, 'in collection');
            }
        }

        function destroyContainer() {
            settings.container.off(unveilNamespace).removeData(unveilString);
            containerContext.images.off(unveilNamespace).removeData(unveilString);
            containerContext.initialized = false;
            containerContext.images = null;
        }

        /**
         * # BOOTSTRAPPING
         * ---
         */

        /**
         * Read and reset the src attribute, to prevent loading of original images
         */
        this.each(function() {
            let $this = $(this),
                elmPlaceholder = $this.data(srcString + '-' + placeholderString) || settings.placeholder;

            // If this element has been called before,
            // don't set placeholder now to prevent FOUI (Flash Of Ustyled Image)
            if (!$this.data(unveilString)) {

                // Add element to global array
                containerContext.images = $(containerContext.images).add(this);

                // Set the unveil settings
                $this.data(unveilString, settings);

                // Set data-src if not set
                if (!$this.data(settings.attribute))
                    $this.data(settings.attribute, $this.prop(settings.attribute));

                // Set placeholder
                $this
                    .one('load' + unveilNamespace, function() {
                        let $this = $(this);

                        if ($this.hasClass(unveilString + '-loaded'))
                            return;

                        $this.addClass(unveilString + '-' + placeholderString);
                        lookup();
                    })
                    .prop(srcString, '')
                    .prop(srcString, elmPlaceholder);
            }
        });

        if (settings.debug) console.log('Images now in collection', containerContext.images.length);

        /**
         * Bind global listeners
         */
        if (!containerContext.initialized) {
            settings.container
                .on('resize' + unveilNamespace, throttle(resize))
                .on('scroll' + unveilNamespace, throttle(lookup))
                .on('lookup' + unveilNamespace, lookup)
                .on('destroy' + unveilNamespace, destroyContainer);
            containerContext.initialized = true;
        }

        /**
         * Wait a little bit for the placeholder to be set, so the src attribute is not empty (which will mark the image as hidden)
         */
        setTimeout(lookup, 0);

        /**
         * That's all folks!
         */
        return this;
    };
}));